import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  getAllCourses,
  getAllConcentrations,
  getAllAasPrograms,
  getTransferMappings,
  getAllInstitutions,
  getPathwayRelationships
} from '@/services/academicService';
import {
  Library,
  RefreshCw,
  LayoutDashboard,
  TableProperties,
  AlertCircle,
  Search
} from 'lucide-react';
import Button from '@/components/common/Button';
import SemesterTimeline from '@/components/advising/SemesterTimeline';
import TransferMatrix from '@/components/advising/TransferMatrix';
import CourseDetailModal from '@/components/modals/CourseDetailModal';
import { toast } from 'react-toastify';
import { isSuperAdmin } from '@/utils/roleUtils';
import './styles.scss';

const AcademicPathwayViewer = ({ institution: defaultInstitution }) => {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [concentrations, setConcentrations] = useState([]);
  const [aasPrograms, setAasPrograms] = useState([]);
  const [selectedInstitution, setSelectedInstitution] = useState(defaultInstitution || '');
  const [selectedAas, setSelectedAas] = useState(null);
  const [selectedBas, setSelectedBas] = useState(null);
  const [pathwayRelations, setPathwayRelations] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [activeView, setActiveView] = useState('roadmap'); // roadmap or matrix
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [detailCourse, setDetailCourse] = useState(null);
  const [requirementTitle, setRequirementTitle] = useState('');

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (defaultInstitution && defaultInstitution !== selectedInstitution) {
      setSelectedInstitution(defaultInstitution);
      fetchConcentrations(defaultInstitution);
    }
  }, [defaultInstitution]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [coursesRes, aasRes, relationsRes] = await Promise.all([
        getAllCourses(),
        getAllAasPrograms(),
        getPathwayRelationships()
      ]);
      setCourses(coursesRes);
      setAasPrograms(aasRes);
      setPathwayRelations(relationsRes);

      // Fetch ALL concentrations initially to ensure a full catalog is available
      // This matches the Academic Hub's robust start state
      const concRes = await getAllConcentrations('all');
      setConcentrations(concRes);
    } catch (error) {
      console.error('Failed to fetch initial academic data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConcentrations = async (inst) => {
    try {
      setLoading(true);
      const concRes = await getAllConcentrations(inst);
      setConcentrations(concRes);
      
      // Selection Sync: Reset target if it's no longer in the filtered list
      if (selectedBas && !concRes.find(c => c.id === selectedBas.id)) {
        setSelectedBas(null);
      }
    } catch (error) {
      console.error('Failed to fetch filtered concentrations:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedAas && selectedBas) {
      fetchMappings();
    }
  }, [selectedAas, selectedBas]);

  useEffect(() => {
    // Selection Sync: If the current target goal is no longer compatible with the new source, reset it
    if (selectedAas && selectedBas && pathwayRelations.length > 0) {
      const isCompatible = pathwayRelations.some(
        rel => rel.aasProgramId === selectedAas.id && rel.basConcentrationId === selectedBas.id
      );
      if (!isCompatible) {
        setSelectedBas(null);
      }
    }
  }, [selectedAas, pathwayRelations, selectedBas]);

  const fetchMappings = async () => {
    try {
      const data = await getTransferMappings(selectedAas.id, selectedBas.id);
      setMappings(data);
    } catch (error) {
      console.error('Failed to fetch mappings:', error);
    }
  };

  const generateRoadmap = () => {
    if (!selectedAas || !selectedBas) return [];
    const roadmap = [];
    const aasCourses = selectedAas.courses || [];

    const programName = selectedAas.name || 'AAS Program';
    const cleanPrefix = programName
      .split(/[\s-]+/)
      .map(w => w[0])
      .join('')
      .replace(/[^a-zA-Z]/g, '')
      .toUpperCase() || 'AAS';

    // Dynamic authentic major core course catalogs based on program domain
    const lowName = programName.toLowerCase();
    let sampleTitles = [
      'Foundations of Applied Technology',
      'Technical Communication & Collaboration',
      'Applied Professional Practices',
      'Specialization Capstone Seminar',
      'Specialized Industry Training I',
      'Specialized Industry Training II',
      'Occupational Specialty Practicum I',
      'Occupational Specialty Practicum II',
      'Introduction to Professional Trades',
      'Industrial Safety & Compliance',
      'Technical Blueprint Analysis',
      'Emerging Technologies Seminar'
    ];

    if (lowName.includes('construction')) {
      sampleTitles = [
        'Introduction to Construction Trades',
        'Safety and OSHA Regulations',
        'Blueprint Reading & Documentation',
        'Construction Materials & Methods',
        'Introduction to CAD and BIM',
        'Construction Estimating Fundamentals',
        'Project Scheduling & Planning',
        'Construction Leadership & Ethics',
        'Building Systems and MEP',
        'Codes and Inspections',
        'Construction Site Management',
        'Sustainable Construction Practices',
        'Construction Specialty Practicum',
        'Construction Trades Capstone'
      ];
    } else if (lowName.includes('manufacturing')) {
      sampleTitles = [
        'Introduction to Manufacturing & Industry 4.0',
        'Industrial Safety and Safety Standards',
        'Technical Drafting and Prints',
        'Properties of Engineering Materials',
        'Basic Electrical & Electronics Systems',
        'Introduction to CNC Programming',
        'Quality Control and Inspection',
        'Fluid Power and Pneumatics',
        'Automation and Robotics',
        'Lean Manufacturing Principles',
        'Production Planning and Control',
        'Advanced Manufacturing Processes',
        'Industrial Operations & Maintenance',
        'Manufacturing Capstone Project'
      ];
    } else if (lowName.includes('information') || lowName.includes('computer') || lowName.includes('it-') || lowName.includes('citc')) {
      sampleTitles = [
        'Introduction to Computer Science & IT',
        'Computer Hardware and Troubleshooting',
        'Introduction to Operating Systems',
        'Network Architecture & Protocols',
        'Database Management Systems',
        'Introduction to Programming & Logic',
        'Web Design and Development',
        'Introduction to Cybersecurity',
        'Systems Analysis and Design',
        'Cloud Computing Foundations',
        'Linux Systems Administration',
        'Information Security Fundamentals',
        'IT Operations & Helpdesk Support',
        'IT Project Management & Capstone'
      ];
    }

    const semRealCourses = [[], [], [], []];
    aasCourses.forEach((c, index) => {
      const semIndex = index % 4;
      semRealCourses[semIndex].push({
        code: c.code,
        title: c.title,
        credits: c.credits || 3,
        status: 'transferred',
        transferDecision: c.transferDecision
      });
    });

    for (let i = 0; i < 4; i++) {
      const semesterNum = i + 1;
      const semCourses = semRealCourses[i];

      // Pad with Technical/Vocational Block Electives until we have 5 courses (representing a full 15-credit load)
      let padIndex = 1;
      while (semCourses.length < 5) {
        const titleIndex = (i * 3 + padIndex - 1) % sampleTitles.length;
        const dynamicTitle = sampleTitles[titleIndex];

        semCourses.push({
          code: `${cleanPrefix} ${1000 + i * 100 + padIndex * 10}`,
          title: dynamicTitle,
          credits: 3,
          status: 'complete',
          transferDecision: `${programName} Technical Block`
        });
        padIndex++;
      }

      roadmap.push({
        name: `Semester ${semesterNum} (AAS)`,
        status: 'complete',
        courses: semCourses
      });
    }

    const aasCodes = new Set(aasCourses.map(c => c.code ? c.code.trim().toUpperCase() : ''));
    const hasBlockTransfer = mappings && mappings.length > 0;

    const upperCore = selectedBas.upperDivisionCore || [];
    const foundation = selectedBas.foundationCourses || [];
    let allBasCourses = [...upperCore, ...foundation]
      .filter((c, index, self) => 
        c && c.courseCode && self.findIndex(t => t.courseCode === c.courseCode) === index
      )
      .filter(c => {
        const cleanC = c.courseCode ? c.courseCode.trim().toUpperCase() : '';
        const isAlreadyTaken = aasCodes.has(cleanC);
        const isTransferred = (mappings || []).some(m => {
          const cleanSource = m.sourceCourseCode ? m.sourceCourseCode.trim().toUpperCase() : '';
          const cleanTarget = m.targetCourseCode ? m.targetCourseCode.trim().toUpperCase() : '';
          return (cleanSource === cleanC || cleanTarget === cleanC);
        });

        // Block transfer dynamically satisfies generic general electives based on metadata
        const isGenericElective = (c.notes || c.title || '').toLowerCase().includes('general elective');
        const isSatisfiedByBlock = hasBlockTransfer && isGenericElective;

        return !isAlreadyTaken && !isTransferred && !isSatisfiedByBlock;
      });

    // 1. Dynamic Deduplication: Group alternative courses that explicitly state a choice in their notes
    const uniqueNotes = new Set();
    let deduplicated = [];
    for (const c of allBasCourses) {
      if (c.notes && c.notes.toLowerCase().includes('choose')) {
        const key = c.notes.toLowerCase().trim();
        if (!uniqueNotes.has(key)) {
          deduplicated.push(c);
          uniqueNotes.add(key);
        }
      } else {
        deduplicated.push(c);
      }
    }
    allBasCourses = deduplicated;

    // 2. Dynamic Workload Sampling: A standard BAS roadmap consists of the remaining 60 credits (~20 courses).
    // If the curriculum lists exhaustive elective pools exceeding this, dynamically sample a representative 20-course path.
    const MAX_ROADMAP_COURSES = 20;
    if (allBasCourses.length > MAX_ROADMAP_COURSES) {
      const step = allBasCourses.length / MAX_ROADMAP_COURSES;
      const sampled = [];
      for (let i = 0; i < MAX_ROADMAP_COURSES; i++) {
        sampled.push(allBasCourses[Math.floor(i * step)]);
      }
      allBasCourses = sampled;
    }

    const coursesPerSemester = Math.ceil(allBasCourses.length / 4) || 4;

    for (let i = 0; i < 4; i++) {
      const semesterNum = i + 5;
      const startIdx = i * coursesPerSemester;
      const semCourses = allBasCourses.slice(startIdx, startIdx + coursesPerSemester).map(c => {
        const isTransferred = (mappings || []).some(m => {
          const cleanSource = m.sourceCourseCode ? m.sourceCourseCode.trim().toUpperCase() : '';
          const cleanTarget = m.targetCourseCode ? m.targetCourseCode.trim().toUpperCase() : '';
          const cleanC = c.courseCode ? c.courseCode.trim().toUpperCase() : '';
          return (cleanSource === cleanC || cleanTarget === cleanC);
        });
        return {
          code: c.courseCode || 'TBD',
          title: c.notes || 'Course Requirement',
          credits: c.credits || 3,
          status: isTransferred ? 'transferred' : 'remaining'
        };
      });

      roadmap.push({
        name: `Semester ${semesterNum} (BAS)`,
        status: 'pending',
        courses: semCourses
      });
    }

    return roadmap;
  };

  const handleCourseClick = (roadmapCourse) => {
    if (!roadmapCourse || !roadmapCourse.code) return;
    const searchCode = roadmapCourse.code.replace(/\s/g, '').toUpperCase();
    const matched = courses.find(c => c.code && c.code.replace(/\s/g, '').toUpperCase() === searchCode);
    setDetailCourse(matched ? { ...roadmapCourse, ...matched } : roadmapCourse);
    setRequirementTitle(roadmapCourse.title);
    setIsModalOpen(true);
  };

  return (
    <div className="academic-pathway-viewer">
      <div className="viewer-card-glass">
        <header className="viewer-header">
          <div className="header-info">
            <div className="header-title-row">
              <h3>Pathway Explorer</h3>
            </div>
            <p className="viewer-subtitle">Visualize transfer mappings and projected semester plans.</p>
          </div>


        </header>

        <div className="selection-grid-glass">
          <div className="select-group">
            <label>Source AAS Program</label>
            <select 
              value={selectedAas?.id || ''}
              onChange={(e) => setSelectedAas(aasPrograms.find(p => p.id === e.target.value))}
            >
              <option value="">Select Student Source...</option>
              {(() => {
                const groups = aasPrograms.reduce((acc, p) => {
                  const group = p.schoolName || p.school || 'General Institution';
                  if (!acc[group]) acc[group] = [];
                  acc[group].push(p);
                  return acc;
                }, {});

                return Object.keys(groups).sort().map(groupName => (
                  <optgroup label={groupName} key={groupName}>
                    {groups[groupName]
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map(p => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))
                    }
                  </optgroup>
                ));
              })()}
            </select>
          </div>
          <div className="select-group">
            <label>Target BAS Concentration</label>
            <select
              value={selectedBas?.id || ''}
              onChange={(e) => setSelectedBas(concentrations.find(c => c.id === e.target.value))}
            >
              <option value="">Select Goal...</option>
              {(() => {
                if (!selectedAas) return concentrations;
                return concentrations.filter(c => 
                  pathwayRelations.some(rel => rel.aasProgramId === selectedAas.id && rel.basConcentrationId === c.id)
                );
              })().map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>

        <div className="viewer-content">
          {loading ? (
            <div className="viewer-loading">
              <div className="pulse-loader"></div>
              <p>Gathering Intelligence...</p>
            </div>
          ) : !selectedAas || !selectedBas ? (
            <div className="empty-state-card">
              <AlertCircle size={40} className="empty-icon" />
              <h4>Selection Required</h4>
              <p>Select a source program and target concentration to view your roadmap.</p>
            </div>
          ) : (
            <div className="data-view-area">
              <section className="matrix-section">
                <div className="section-header">
                  <TableProperties size={20} />
                  <h4>Course Equivalency Map</h4>
                </div>
                <div className="matrix-scroll-wrapper">
                  <TransferMatrix mappings={mappings} basName={selectedBas?.fullName || selectedBas?.name} />
                </div>
              </section>

              <section className="roadmap-section">
                <div className="section-header">
                  <LayoutDashboard size={20} />
                  <h4>Semester-by-Semester Roadmap</h4>
                  <div className="roadmap-legend">
                    <div className="legend-item"><span className="dot transferred"></span><label>Direct Transfer</label></div>
                    <div className="legend-item"><span className="dot complete"></span><label>AAS Tech Block</label></div>
                    <div className="legend-item"><span className="dot remaining"></span><label>Remaining Course</label></div>
                  </div>
                </div>
                <div className="roadmap-scroll-wrapper">
                  <SemesterTimeline 
                    data={generateRoadmap()} 
                    onCourseClick={handleCourseClick}
                  />
                </div>
              </section>
            </div>
          )}
        </div>
      </div>

      <CourseDetailModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        course={detailCourse}
        requirementTitle={requirementTitle}
      />
    </div>
  );
};

export default AcademicPathwayViewer;
